import { supabase, invokeFunction } from '@/lib/supabase';
import { resolveActiveMembership } from '@/services/household.service';
import {
  isMissingFunctionError,
  isSchemaNotReadyError,
} from '@/lib/deployWindow';
import type {
  UserProfile,
  UserProfileUpdate,
  CaregiverRole,
  NotificationPreferences,
  NotificationPreferencesUpdate,
  SmsContactChannel,
  DigestDeliveryFailure,
} from '@/types';

async function getDbUserId(): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('users')
    .select('id')
    .eq('supabase_id', user.id)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('User profile not found');
  return data.id;
}

export async function getProfile(): Promise<UserProfile | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('users')
    .select()
    .eq('supabase_id', user.id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function updateProfile(
  updates: UserProfileUpdate,
): Promise<UserProfile> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('supabase_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data!;
}

/**
 * The caller's family role ("Mom", "Grandpa", …) in one household (YAR-485).
 * `null` when they have not given one there yet — an invitee joining a second
 * household starts empty rather than inheriting the first household's answer.
 */
export async function getMyFamilyRole(
  householdId: string,
): Promise<string | null> {
  const userId = await getDbUserId();
  const { data, error } = await supabase
    .from('household_members')
    .select('family_role')
    .eq('household_id', householdId)
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  if (error && isSchemaNotReadyError(error)) {
    // Deploy-window guard (lib/deployWindow.ts): before YAR-485's migration
    // the role lived on the account, users.role.
    const legacy = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .maybeSingle();
    if (legacy.error) throw legacy.error;
    return legacy.data?.role?.trim() || null;
  }
  if (error) throw error;
  return data?.family_role ?? null;
}

/**
 * Set the caller's family role in one household. The RPC binds the write to
 * the caller's own ACTIVE membership; `householdId` only picks which of their
 * memberships. A blank role clears it.
 */
export async function setMyFamilyRole(
  householdId: string,
  role: string,
): Promise<string | null> {
  const { data, error } = await supabase.rpc('set_my_family_role', {
    p_household_id: householdId,
    p_role: role,
  });
  if (error && isMissingFunctionError(error)) {
    // Deploy-window guard (lib/deployWindow.ts): no RPC yet, so write the
    // account-level users.role the previous release used.
    const userId = await getDbUserId();
    const trimmed = role.trim() || null;
    const { error: legacyError } = await supabase
      .from('users')
      .update({ role: trimmed })
      .eq('id', userId);
    if (legacyError) throw legacyError;
    return trimmed;
  }
  if (error) throw error;
  return data ?? null;
}

export async function getNotificationPreferences(): Promise<NotificationPreferences | null> {
  const userId = await getDbUserId();

  const { data, error } = await supabase
    .from('notification_preferences')
    .select()
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function updateNotificationPreferences(
  updates: NotificationPreferencesUpdate,
): Promise<NotificationPreferences> {
  const userId = await getDbUserId();

  const { data, error } = await supabase
    .from('notification_preferences')
    .update(updates)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data!;
}

/**
 * Read the current user's SMS contact channel (verification + opt-out state),
 * or null if none. Prefers a verified row when several exist. RLS scopes this
 * to the caller; the client can read but never write this table.
 */
export async function getSmsContactChannel(): Promise<SmsContactChannel | null> {
  const userId = await getDbUserId();

  const { data, error } = await supabase
    .from('user_contact_channels')
    .select('address, verified_at, consent_at, opted_out_at')
    .eq('user_id', userId)
    .eq('channel', 'sms')
    .order('verified_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Read the caller's own latest digest-SMS delivery, but ONLY when it
 * terminally failed (server-side filter — see get_my_latest_digest_failure()
 * in 20260706130001_get_my_latest_digest_failure_rpc.sql). `null` covers
 * both "no digest sent yet" and "latest attempt succeeded/was skipped" —
 * the caller doesn't need to distinguish those for the banner.
 * notification_deliveries is service-role-only (PII), so this narrow
 * SECURITY DEFINER RPC is the read path rather than an RLS policy on the
 * full ledger.
 */
export async function getLatestDigestFailure(): Promise<DigestDeliveryFailure | null> {
  const { data, error } = await supabase.rpc('get_my_latest_digest_failure');
  if (error) throw error;
  return data?.[0] ?? null;
}

export interface StartPhoneVerificationInput {
  e164: string;
  consentAccepted: boolean;
  consentVersion: string;
  consentText: string;
  /** Consent-event attribution for the TCPA audit trail. Defaults to
   * 'settings_web' server-side when omitted — pass 'onboarding' from the
   * onboarding digest-opt-in step so the source isn't mislabeled. */
  source?: 'settings_web' | 'onboarding';
}

/** Begin SMS verification (Twilio Verify) and record the user's opt-in consent. */
export async function startPhoneVerification(
  input: StartPhoneVerificationInput,
): Promise<void> {
  const { error } = await invokeFunction('phone-verify-start', {
    body: input,
  });
  if (error) throw error;
}

/** Complete SMS verification with the received code. */
export async function checkPhoneVerification(input: {
  e164: string;
  code: string;
}): Promise<{ verified: boolean }> {
  const { data, error } = await invokeFunction('phone-verify-check', {
    body: input,
  });
  if (error) throw error;
  return (data as { verified: boolean }) ?? { verified: false };
}

/**
 * Remove the caller's own SMS phone number. Server-side (phone-verify-remove)
 * deletes the `user_contact_channels` row while preserving consent history in
 * `notification_consent_events`, and turns off `sms_enabled` +
 * `digest_sms_enabled` — removing the number always deactivates the digest
 * too (YAR-265 AC7).
 */
export async function removePhoneNumber(): Promise<void> {
  const { error } = await invokeFunction('phone-verify-remove', { body: {} });
  if (error) throw error;
}

export async function getUserRole(
  householdId?: string | null,
): Promise<CaregiverRole | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: dbUser, error: dbErr } = await supabase
    .from('users')
    .select('id')
    .eq('supabase_id', user.id)
    .maybeSingle();

  if (dbErr) throw dbErr;
  if (!dbUser) return null;

  // Explicit scoping: caller knows which household they care about.
  if (householdId) {
    const { data: membership, error: memErr } = await supabase
      .from('household_members')
      .select('role')
      .eq('user_id', dbUser.id)
      .eq('household_id', householdId)
      .maybeSingle();
    if (memErr) throw memErr;
    return (membership?.role as CaregiverRole) ?? null;
  }

  // No explicit household — resolve the ACTIVE one: the household the user
  // last had open (localStorage pin) when they still belong to it, else the
  // oldest. A user may belong to SEVERAL households (only ownership is
  // singular) and their access level is PER-household — owner of theirs,
  // viewer in another — so the role MUST come from the same household
  // AppShell is displaying. Both go through
  // household.service.ts's resolveActiveMembership (also used by
  // email.service.ts's getHouseholdIdForCurrentUser) so they can never
  // diverge.
  const membership = await resolveActiveMembership(dbUser.id);
  return membership?.role ?? null;
}

export async function deleteAccount(reason?: string): Promise<void> {
  const { error } = await invokeFunction('users-me', {
    method: 'DELETE',
    body: { reason },
  });

  if (error) throw error;
}
